import { useMutation } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

export const CREATE_CUSTOMER_PORTAL_SESSION = graphql(/* GraphQL */ `
  mutation createCustomerPortalSession(
    $organizationId: OrganizationId!
    $returnURL: String!
    $cta: String
  ) {
    createCustomerPortalSession(
      input: {
        organizationId: $organizationId
        returnURL: $returnURL
        cta: $cta
      }
    ) {
      ... on CreateCustomerPortalSessionResponse {
        customerPortalSessionUrl
      }
      ... on BillingError {
        userFriendlyMessage
      }
    }
  }
`)

const DEFAULT_ERROR_MESSAGE =
  'Whoops! Looks like there was a problem processing your request. Please try again. If you continue experiencing this problem, please contact us.'

/**
 * Custom hook to get the Stripe customer portal session
 * @returns {Object} - The customer portal session URL
 */
export const useGetPortalSession = (): {
  getCustomerPortalSession: (args: { returnURL: string; cta: string }) => void
  loading: boolean
} => {
  const organizationId = useOrganizationId()

  const [createPortalSession, { loading }] = useMutation(
    CREATE_CUSTOMER_PORTAL_SESSION,
    {
      onCompleted(data) {
        const response = data.createCustomerPortalSession
        if ('userFriendlyMessage' in response) {
          toast.error(DEFAULT_ERROR_MESSAGE)
        } else {
          window.open(response.customerPortalSessionUrl, '_self')
        }
      },
      onError() {
        toast.error(DEFAULT_ERROR_MESSAGE)
      },
    },
  )

  const getCustomerPortalSession = ({
    returnURL,
    cta,
  }: {
    returnURL: string
    cta: string
  }): void => {
    if (!organizationId) {
      toast.error(DEFAULT_ERROR_MESSAGE)
      return
    }
    createPortalSession({
      variables: {
        organizationId,
        returnURL,
        cta,
      },
    })
  }

  return {
    getCustomerPortalSession,
    loading,
  }
}
